/**
 * Which grocery-list items actually landed in her Kroger cart.
 *
 * This exists because the answer is NOT "everything on the list" and NOT even
 * "everything we sent". Three sets shrink on the way to the cart:
 *
 *   her list  ->  rows Kroger found AND she left ticked  ->  rows that added OK
 *
 * Until Aug 16 2026 `handleCartSuccess` ticked off every unchecked row the
 * moment a single item was added, which struck through items Kroger never
 * found, items she deliberately unticked in the pricing panel, and items that
 * failed mid-add while their failure was on screen. Her list is what she shops
 * from, so a false checkmark there is the same class of lie as a false
 * "Saved ✓" — and "Clear checked" one tap away turns it into data loss.
 *
 * Matching is by productId, not name: the cart function reports failures with
 * Kroger's own `productName` (its catalog description), which is not the text
 * her grocery_list row is keyed on.
 *
 * ⚠️ The ANSWER is row ids, not text (#942). `grocery_list` has no unique
 * constraint on (user_id, item) — see 20260415043009, which creates the table
 * with `item TEXT NOT NULL` and nothing else — so duplicate rows with identical
 * text are legitimate and expected (#743's auto-fill makes them likelier: two
 * recipes can each contribute the same staple). Answering in text meant
 * `.in("item", …)` could not tell two "milk" rows apart and ticked off BOTH
 * when one went into the cart — the same family as the whole-list bug fixed on
 * Aug 16 2026, two rows instead of the whole list. Duplicates are NOT
 * deduplicated anywhere: the fix is row identity, not deduplication.
 */

/** The shape this helper needs from a KrogerPricing ProductResult. */
export interface SentItem {
  /** The ORIGINAL list-item text — what grocery_list.item holds. */
  item: string;
  productId?: string;
  /**
   * The grocery_list row this came from. Absent when the caller could not
   * positively identify one — the chat-side panel has no rows at all, and the
   * index↔row correlation is dropped rather than guessed if it ever stops
   * lining up. Absent means "not confirmed", which means not ticked.
   */
  rowId?: string;
}

/** The shape this helper needs from a kroger-cart `failed[]` entry. */
export interface FailedItem {
  productId?: string;
}

/**
 * Tie each search result back to the grocery_list row that produced it.
 *
 * Row identity rides the INDEX across the round trip: `kroger-prices` returns
 * `results` strictly 1:1 and in order with the `items` it was sent (ordered
 * batches of 5, `Promise.all` per batch), and KrogerPricing already treats the
 * index as row identity for its per-row checkboxes. Nothing about the row is
 * sent over the wire.
 *
 * ⚠️ The text re-check is the guard on that assumption, not decoration. If
 * kroger-prices ever filters, reorders or dedupes its results, the index would
 * silently point at a DIFFERENT row — and ticking off the wrong row is worse
 * than the duplicate-text bug this exists to fix. A mismatch (or an index past
 * the end of either array) yields NO id, and no id means the row stays
 * unchecked.
 *
 * @param sourceItems what was sent, in order; `id` absent for text-only callers
 * @param results     what came back, expected 1:1 with sourceItems
 * @param indices     the subset of indices actually handed to the cart
 */
export function attachRowIds<T extends { item: string }>(
  sourceItems: readonly { id?: string; item: string }[],
  results: readonly T[],
  indices: readonly number[]
): Array<T & { rowId?: string }> {
  return indices.map((idx) => {
    const src = sourceItems[idx];
    const r = results[idx];
    return { ...r, rowId: src && r && src.item === r.item ? src.id : undefined };
  });
}

/**
 * @param sent   the items handed to the cart function (already filtered to
 *               found + ticked by the caller), each carrying the grocery_list
 *               row it came from
 * @param failed the cart function's `failed` array
 * @returns grocery_list row ids that reached the cart, deduped
 */
export function deriveAddedRowIds(
  sent: readonly SentItem[],
  failed: readonly FailedItem[] | null | undefined
): string[] {
  const failedIds = new Set((failed || []).map((f) => f.productId));
  // Fail CLOSED on a missing productId: the cart function rejects such an item
  // outright ("No product ID"), so it cannot have been added. Leaving it
  // unchecked is the harmless direction — she re-adds it. Ticking it off a list
  // she is standing in a store reading is not.
  //
  // This also neutralises the cart function's productId: "" failure rows: any
  // sent row a blank id could match is already excluded by the same check, so
  // there is deliberately no separate filter for them. Do not add one back
  // without a test that fails when it is removed — the obvious one cannot.
  //
  // Fail CLOSED on a missing rowId for the same reason: an item we cannot tie
  // to a specific row cannot be ticked off without guessing which row it was,
  // and guessing is the entire bug this function exists to stop.
  const added = sent
    .filter((r) => !!r.productId && !failedIds.has(r.productId) && !!r.rowId)
    .map((r) => r.rowId!);
  // Two rows with the SAME text are two distinct ids and both survive this —
  // that is the point. Dedup is per-row (one row cannot land twice), never per
  // text.
  return Array.from(new Set(added));
}
